import React, { useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { useFormik, getIn } from 'formik';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';

import { formatNumberThsndAndDec, formatNumberMagnitude } from '../helpers/stringFormat';
import { getMagnitude } from './valueFunctions';
import { addCurrencyFrequency } from '../helpers/currencyFormat';
import { Typography } from '@mui/material';
import { GreenRedCompactSwitch, TextFieldCustom } from '../helpers/formFields';
import { createSliderMarks } from './formFunctions';
import { generalTooltipText, tooltip } from '../helpers/tooltip';


const RiskValueDriverLitigationForm = forwardRef(({ currency, outcome, outcomeList, ownProduct, riskStructure, editLitigationDriverId, editLitigationDriver, formActions, onSubmit }, ref) => {
    const [open, setOpen] = useState(false);
    const [openOutcome, setOpenOutcome] = useState(false);
    const [selectedType, setSelectedType] = useState(riskStructure[0].type)
    const [positiveDriver, setPositiveDriver] = useState(true);

    const tooltipText = {
        riskChange: `Risk ${positiveDriver ? "Reduction" : "Increase"} when using your product instead of the competitive alternative.`,
    }

    let riskStructureSelected = riskStructure.find(type => type.type === selectedType)

    const handleTypeChange = (event) => {
        setSelectedType(event.target.value);

    };

    const handleOutcomeChange = (event) => {
        formik.setFieldValue('outcome', event.target.value)
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleOutcomeClose = () => {
        setOpenOutcome(false);
    };

    const handleOutcomeOpen = () => {
        setOpenOutcome(true);
    };

    useEffect(() => {
        setSelectedType(riskStructure[0].type)
        setPositiveDriver(true)

        formik.setFieldValue('type', riskStructure[0].type)
        formik.setFieldValue("likelihood", riskStructure[0].likelihood.lower)
        formik.setFieldValue("compliance_costs", riskStructure[0].impact.compliance_costs?.lower || 0)
        formik.setFieldValue("operational_costs", riskStructure[0].impact.operational_costs?.lower || 0)
        formik.setFieldValue("legal_costs", riskStructure[0].impact.legal_costs?.lower || 0)
        formik.setFieldValue("reputational_costs", riskStructure[0].impact.reputational_costs?.lower || 0)
        formik.setFieldValue("settlement_costs", riskStructure[0].impact.settlement_costs?.lower || 0)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [riskStructure]);

    useEffect(() => {
        formik.setFieldValue("outcome", outcomeList[0])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outcomeList]);

    useEffect(() => {
        if (editLitigationDriverId) {
            setPositiveDriver(editLitigationDriver["positive_driver"])
            setSelectedType(editLitigationDriver["type"])

            formik.setFieldValue('type', editLitigationDriver["type"])
            formik.setFieldValue("likelihood", editLitigationDriver["likelihood"])
            formik.setFieldValue("compliance_costs", editLitigationDriver["compliance_costs"])
            formik.setFieldValue("operational_costs", editLitigationDriver["operational_costs"])
            formik.setFieldValue("legal_costs", editLitigationDriver["legal_costs"])
            formik.setFieldValue("reputational_costs", editLitigationDriver["reputational_costs"])
            formik.setFieldValue("settlement_costs", editLitigationDriver["settlement_costs"])
            formik.setFieldValue("risk_reduction", editLitigationDriver["risk_reduction"])
            formik.setFieldValue("positive_driver", editLitigationDriver["positive_driver"])
            formik.setFieldValue("outcome", editLitigationDriver["outcome"])
            formik.setFieldValue("driver_name", editLitigationDriver["driver_name"])
            formik.setFieldValue("comment", editLitigationDriver["comment"])
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editLitigationDriverId])

    useEffect(() => {
        if (!editLitigationDriverId) {
            formik.setFieldValue("driver_name", `${selectedType} Risk ${positiveDriver ? 'Reduction' : 'Increase'}`)

            formik.setFieldValue('type', selectedType)
            formik.setFieldValue("likelihood", riskStructureSelected.likelihood.lower)
            formik.setFieldValue("compliance_costs", riskStructureSelected.impact.compliance_costs?.lower || 0)
            formik.setFieldValue("operational_costs", riskStructureSelected.impact.operational_costs?.lower || 0)
            formik.setFieldValue("legal_costs", riskStructureSelected.impact.legal_costs?.lower || 0)
            formik.setFieldValue("reputational_costs", riskStructureSelected.impact.reputational_costs?.lower || 0)
            formik.setFieldValue("settlement_costs", riskStructureSelected.impact.settlement_costs?.lower || 0)
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positiveDriver, selectedType])

    const initialValues = {
        id: null,
        risk_type: 'litigation',
        outcome: '',
        driver_name: `${selectedType} Risk ${positiveDriver ? 'Reduction' : 'Increase'}`,
        comment: '',
        positive_driver: true,
        type: riskStructure[0].type,
        currency: currency,
        likelihood: riskStructure[0].likelihood.lower,
        compliance_costs: riskStructure[0].impact.compliance_costs?.lower || 0,
        operational_costs: riskStructure[0].impact.operational_costs?.lower || 0,
        legal_costs: riskStructure[0].impact.legal_costs?.lower || 0,
        reputational_costs: riskStructure[0].impact.reputational_costs?.lower || 0,
        settlement_costs: riskStructure[0].impact.settlement_costs?.lower || 0,
        risk_reduction: 0,
    }

    const isNumeric = (value) => {
        return /^-?\d{1,3}(,\d{3})*(\.\d+)?$/.test(value);
    };

    const isPositive = (value) => {
        return parseFloat(value.toString().replace(/,/g, '')) >= 0;
    };

    const validate = (values) => {
        const errors = {};

        if (!values.driver_name) {
            errors.driver_name = 'Driver must have a name';
        }

        if (!isNumeric(values.likelihood)) {
            errors.likelihood = 'Must be numeric value';
        } else if (!isPositive(values.likelihood)) {
            errors.likelihood = 'Must be positive or zero';
        }

        // if (!isNumeric(values.rate_value)) {
        //     errors.rate_value = 'Must be numeric value';
        // } else if (!isPositive(values.rate_value)) {
        //     errors.rate_value = 'Must be positive or zero';
        // } else if (values.rate_value > 100) {
        //     errors.rate_value = 'Not more than 100';
        // }

        // if (!isNumeric(values.likelihood_violation)) {
        //     errors.likelihood_violation = 'Must be numeric value';
        // } else if (!isPositive(values.likelihood_violation)) {
        //     errors.likelihood_violation = 'Must be positive or zero';
        // } else if (values.likelihood_violation > 100) {
        //     errors.likelihood_violation = 'Not more than 100';
        // }

        // if (!isNumeric(values.likelihood_fine)) {
        //     errors.likelihood_fine = 'Must be numeric value';
        // } else if (!isPositive(values.likelihood_fine)) {
        //     errors.likelihood_fine = 'Must be positive or zero';
        // } else if (values.likelihood_fine > 100) {
        //     errors.likelihood_fine = 'Not more than 100';
        // }

        if (!isNumeric(values.risk_reduction)) {
            errors.risk_reduction = 'Must be numeric value';
        } else if (!isPositive(values.risk_reduction)) {
            errors.risk_reduction = 'Must be positive or zero';
        }

        return errors
    }

    const formik = useFormik({
        initialValues: initialValues,
        validate: validate,
        onSubmit: onSubmit,
    });

    useImperativeHandle(ref, () => ({
        resetForm() {
            formik.handleReset()
            setSelectedType(riskStructure[0].type)
            setPositiveDriver(true)
            if (!outcome) {
                formik.setFieldValue("outcome", outcomeList[0] || '')
            }
            formik.setFieldValue('type', riskStructure[0].type)
            formik.setFieldValue("likelihood", riskStructure[0].likelihood.lower)
            formik.setFieldValue("compliance_costs", riskStructure[0].impact.compliance_costs?.lower || 0)
            formik.setFieldValue("operational_costs", riskStructure[0].impact.operational_costs?.lower || 0)
            formik.setFieldValue("legal_costs", riskStructure[0].impact.legal_costs?.lower || 0)
            formik.setFieldValue("reputational_costs", riskStructure[0].impact.reputational_costs?.lower || 0)
            formik.setFieldValue("settlement_costs", riskStructure[0].impact.settlement_costs?.lower || 0)
            formik.setFieldValue("risk_reduction", 0)
        },
    }));

    return (
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ ml: 2, maxWidth: 1200 }}>
            <Grid container columnSpacing={2} rowSpacing={2} sx={{ mt: 1 }}>
                {riskStructure.map((type, index) => {
                    if (type.type === selectedType) {
                        return (
                            <React.Fragment key={index}>
                                <Grid item xs={12}>
                                    <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                            1. Select Type
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={2}>
                                    <Stack direction="column" gap={1}>
                                        <Typography variant="caption">
                                            Type
                                        </Typography>
                                        <Box>
                                            <Select
                                                open={open}
                                                onClose={handleClose}
                                                onOpen={handleOpen}
                                                value={formik.values.type}
                                                onChange={handleTypeChange}
                                                size='small'
                                            >
                                                {riskStructure.map((type, index) => (
                                                    <MenuItem key={index} value={type.type}>
                                                        {type.type}{tooltip(type.description)}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </Box>
                                    </Stack>
                                </Grid>
                                <Grid item xs={10} />
                                <Grid item xs={12}>
                                    <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                            2. Likelihood
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={2}>
                                    <Stack direction="column">
                                        <Typography variant="caption" sx={{ mr: 1 }}>
                                            Annual Likelihood{tooltip(type.likelihood.description)}
                                        </Typography>
                                        <TextFieldCustom
                                            name="likelihood"
                                            endAdornment={
                                                <Typography variant="caption">
                                                    %
                                                </Typography>
                                            }
                                            value={formik.values.likelihood}
                                            onChange={(event) => {
                                                const { value } = event.target
                                                const formattedValue = formatNumberThsndAndDec(value)
                                                formik.setFieldValue("likelihood", formattedValue || '')
                                            }}
                                            onBlur={formik.handleBlur}
                                            error={getIn(formik.errors, "likelihood") && Boolean(getIn(formik.errors, "likelihood"))}
                                            helperText={getIn(formik.touched, "likelihood") && getIn(formik.errors, "likelihood")}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid
                                    item
                                    xs={2}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-end',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Likelihood typically varies from {type.likelihood.lower + '%'} to {type.likelihood.upper + '%'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={8} />
                                <Grid item xs={12}>
                                    <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                            3. Impact
                                        </Typography>
                                    </Box>
                                </Grid>
                                {Object.keys(type.impact).includes('compliance_costs') &&
                                    <Grid item xs={6}>
                                        <Stack direction="column" gap={1}>
                                            <Typography variant="caption">
                                                Compliance Costs{tooltip(type.impact.compliance_costs.description)}
                                            </Typography>
                                            < Slider
                                                name="compliance_costs"
                                                value={formik.values.compliance_costs}
                                                onChange={formik.handleChange}
                                                valueLabelDisplay="auto"
                                                marks={createSliderMarks(type.impact.compliance_costs)}
                                                min={type.impact.compliance_costs.lower * 0}
                                                max={type.impact.compliance_costs.upper * 1.2}
                                                step={getMagnitude(type.impact.compliance_costs.lower) / 10}
                                                valueLabelFormat={value => addCurrencyFrequency(formatNumberMagnitude(value), type.impact.compliance_costs.currency, null)}
                                                sx={{ maxWidth: 250 }}
                                            />
                                        </Stack>
                                    </Grid>
                                }
                                {Object.keys(type.impact).includes('operational_costs') &&
                                    <Grid item xs={6}>
                                        <Stack direction="column" gap={1}>
                                            <Typography variant="caption">
                                                Operational Costs{tooltip(type.impact.operational_costs.description)}
                                            </Typography>
                                            < Slider
                                                name="operational_costs"
                                                value={formik.values.operational_costs}
                                                onChange={formik.handleChange}
                                                valueLabelDisplay="auto"
                                                marks={createSliderMarks(type.impact.operational_costs)}
                                                min={type.impact.operational_costs.lower * 0}
                                                max={type.impact.operational_costs.upper * 1.2}
                                                step={getMagnitude(type.impact.operational_costs.lower) / 10}
                                                valueLabelFormat={value => addCurrencyFrequency(formatNumberMagnitude(value), type.impact.operational_costs.currency, null)}
                                                sx={{ maxWidth: 250 }}
                                            />
                                        </Stack>
                                    </Grid>
                                }
                                {Object.keys(type.impact).includes('legal_costs') &&
                                    <Grid item xs={6}>
                                        <Stack direction="column" gap={1}>
                                            <Typography variant="caption">
                                                Legal Costs{tooltip(type.impact.legal_costs.description)}
                                            </Typography>
                                            < Slider
                                                name="legal_costs"
                                                value={formik.values.legal_costs}
                                                onChange={formik.handleChange}
                                                valueLabelDisplay="auto"
                                                marks={createSliderMarks(type.impact.legal_costs)}
                                                min={type.impact.legal_costs.lower * 0}
                                                max={type.impact.legal_costs.upper * 1.2}
                                                step={getMagnitude(type.impact.legal_costs.lower) / 10}
                                                valueLabelFormat={value => addCurrencyFrequency(formatNumberMagnitude(value), type.impact.legal_costs.currency, null)}
                                                sx={{ maxWidth: 250 }}
                                            />
                                        </Stack>
                                    </Grid>
                                }
                                {Object.keys(type.impact).includes('settlement_costs') &&
                                    <Grid item xs={6}>
                                        <Stack direction="column" gap={1}>
                                            <Typography variant="caption">
                                                Settlement Costs{tooltip(type.impact.settlement_costs.description)}
                                            </Typography>
                                            < Slider
                                                name="settlement_costs"
                                                value={formik.values.settlement_costs}
                                                onChange={formik.handleChange}
                                                valueLabelDisplay="auto"
                                                marks={createSliderMarks(type.impact.settlement_costs)}
                                                min={type.impact.settlement_costs.lower * 0}
                                                max={type.impact.settlement_costs.upper * 1.2}
                                                step={getMagnitude(type.impact.settlement_costs.lower) / 10}
                                                valueLabelFormat={value => addCurrencyFrequency(formatNumberMagnitude(value), type.impact.settlement_costs.currency, null)}
                                                sx={{ maxWidth: 250 }}
                                            />
                                        </Stack>
                                    </Grid>
                                }
                                {Object.keys(type.impact).includes('reputational_costs') &&
                                    <Grid item xs={6}>
                                        <Stack direction="column" gap={1}>
                                            <Typography variant="caption">
                                                Reputational Costs{tooltip(type.impact.reputational_costs.description)}
                                            </Typography>
                                            < Slider
                                                name="reputational_costs"
                                                value={formik.values.reputational_costs}
                                                onChange={formik.handleChange}
                                                valueLabelDisplay="auto"
                                                marks={createSliderMarks(type.impact.reputational_costs)}
                                                min={type.impact.reputational_costs.lower * 0}
                                                max={type.impact.reputational_costs.upper * 1.2}
                                                step={getMagnitude(type.impact.reputational_costs.lower) / 10}
                                                valueLabelFormat={value => addCurrencyFrequency(formatNumberMagnitude(value), type.impact.reputational_costs.currency, null)}
                                                sx={{ maxWidth: 250 }}
                                            />
                                        </Stack>
                                    </Grid>
                                }
                                {Object.keys(type.impact).length % 2 === 0 ?
                                    null
                                    :
                                    <Grid item xs={6} />
                                }
                                <Grid item xs={2}>
                                    <Stack direction="column">
                                        <Typography variant="caption" sx={{ mr: 1 }}>
                                            {`Risk ${positiveDriver ? 'Reduction' : 'Increase'}`}{tooltip(tooltipText.riskChange)}
                                        </Typography>
                                        <TextFieldCustom
                                            name="risk_reduction"
                                            endAdornment={
                                                <Typography variant="caption">
                                                    %
                                                </Typography>
                                            }
                                            value={formik.values.risk_reduction}
                                            onChange={(event) => {
                                                const { value } = event.target
                                                const formattedValue = formatNumberThsndAndDec(value)
                                                formik.setFieldValue("risk_reduction", formattedValue || '')
                                            }}
                                            onBlur={formik.handleBlur}
                                            error={getIn(formik.errors, "risk_reduction") && Boolean(getIn(formik.errors, "risk_reduction"))}
                                            helperText={getIn(formik.touched, "risk_reduction") && getIn(formik.errors, "risk_reduction")}
                                            greenBackground={positiveDriver}
                                            redBackground={!positiveDriver}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={10} />
                                <Grid item xs={12}>
                                    <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                            4. Driver details
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={2}>
                                    <Stack direction="column" gap={1}>
                                        <Typography variant="caption" sx={{ mr: 1 }}>
                                            Value driver{tooltip(generalTooltipText.differentiationValue)}
                                        </Typography>
                                        <GreenRedCompactSwitch
                                            name="positive_driver"
                                            leftLabel="Negative"
                                            rightLabel="Positive"
                                            value={formik.values.positive_driver}
                                            onChange={(event) => {
                                                formik.handleChange(event)
                                                setPositiveDriver(!positiveDriver)
                                            }}
                                        />

                                    </Stack>
                                </Grid>
                                <Grid item xs={10} />
                                {!outcome &&
                                    (<React.Fragment>
                                        <Grid item xs={2}>
                                            <Stack direction="column" gap={1}>
                                                <Typography variant="caption">
                                                    Outcome
                                                </Typography>
                                                <Box>
                                                    <Select
                                                        open={openOutcome}
                                                        onClose={handleOutcomeClose}
                                                        onOpen={handleOutcomeOpen}
                                                        value={formik.values.outcome}
                                                        onChange={handleOutcomeChange}
                                                        size='small'
                                                    >
                                                        {outcomeList.map((outcome) => (
                                                            <MenuItem key={outcome} value={outcome}>
                                                                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                                                    {outcome}
                                                                </Box>
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </Box>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={10} />
                                    </React.Fragment>)
                                }
                                <Grid item xs={12}>
                                    <Grid container columnSpacing={2}>
                                        <Grid item xs={3}>
                                            <Typography variant="caption" sx={{ mr: 1 }}>
                                                Name of driver
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={5}>
                                            <Typography variant="caption" sx={{ mr: 1 }}>
                                                Comment
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={3} />
                                        <Grid item xs={3}>
                                            <TextFieldCustom
                                                name="driver_name"
                                                value={formik.values.driver_name}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={getIn(formik.errors, "driver_name") && Boolean(getIn(formik.errors, "driver_name"))}
                                                helperText={getIn(formik.touched, "driver_name") && getIn(formik.errors, "driver_name")}
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={5}>
                                            <TextFieldCustom
                                                name="comment"
                                                value={formik.values.comment}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={getIn(formik.errors, "comment") && Boolean(getIn(formik.errors, "comment"))}
                                                helperText={getIn(formik.touched, "comment") && getIn(formik.errors, "comment")}
                                                fullWidth
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </React.Fragment>
                        )
                    }
                    else {
                        return null;
                    }
                })
                }
            </Grid>
            <Box sx={{ mt: 3, mb: 4 }}>
                {formActions()}
            </Box>
        </Box>
    )

})

export default RiskValueDriverLitigationForm                